import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { fromServerDate } from '../../../common/utils/dateUtils';
import { numberOr } from '../../../common/utils/numberUtils';
import { CurrencyFormatWithTitle } from '../../../components/domainSpecific/currencyElements';
import { EMPTY_VALUE } from '../../../common/utils/formatUtils';

const createDateInvoiceColumns = onOpenAmountClick => [
  {
    key: 'invoiceNumber',
    title: <FormattedMessage id="invoice.invoiceNumber" />,
    dataIndex: 'invoiceNumber',
    width: '20%',
    clickable: true,
    ellipsis: true,
  },
  {
    key: 'invoiceDate',
    title: <FormattedMessage id="invoice.invoiceDate" />,
    dataIndex: 'invoiceDate',
    width: '20%',
    ellipsis: true,
    render: text => {
      const date = fromServerDate(text);
      const formattedDate = date.isValid()
        ? date.format('MMM DD YYYY').toUpperCase()
        : EMPTY_VALUE;

      return <span title={formattedDate}>{formattedDate}</span>;
    },
  },
  {
    key: 'noOfJobs',
    title: <FormattedMessage id="invoice.numberOfJobs" />,
    dataIndex: 'noOfJobs',
    width: '20%',
  },
  {
    key: 'originalAmount',
    title: <FormattedMessage id="invoice.originalAmount" />,
    dataIndex: 'originalAmount',
    width: '20%',
    ellipsis: true,
    render: (value, record) => {
      const amount = numberOr(Number(value), null);
      if (amount === null) return EMPTY_VALUE;

      const currencyCode = record?.currency || 'USD';

      return (
        <span className="font-weight-medium">
          <CurrencyFormatWithTitle value={amount} currency={currencyCode} />
        </span>
      );
    },
  },
  {
    key: 'openAmount',
    title: <FormattedMessage id="invoice.openAmount" />,
    dataIndex: 'openAmount',
    width: '20%',
    clickable: true,
    ellipsis: true,
    render: (value, record) => {
      const amount = numberOr(Number(value), null);
      if (amount === null) return EMPTY_VALUE;

      const { invoiceNumber, currency = 'USD' } = record;

      const handleClick = e => {
        e.stopPropagation();
        if (value && invoiceNumber && onOpenAmountClick) {
          onOpenAmountClick({ invoiceNumber, invoiceCurrency: currency });
        }
      };

      return (
        <span className="clickable-cell" onClick={handleClick}>
          <CurrencyFormatWithTitle value={amount} currency={currency} />
        </span>
      );
    },
  },
];

const transformDateInvoiceData = data => {
  if (!data?.length) return [];

  return data.flatMap((invoice, invoiceIndex) => {
    if (invoice.invoiceNumber?.length) {
      return invoice.invoiceNumber.map((invoiceNumber, rowIndex) => {
        const uniqueId = `${invoiceIndex}-${rowIndex}-${invoiceNumber.invoiceNumber}`;
        return {
          ...invoiceNumber,
          id: uniqueId,
          key: uniqueId,
        };
      });
    }

    return [];
  });
};

export const useInvoiceDateTable = (data, onOpenAmountClick, onRowClick) => {
  const invoiceDateColumns = useMemo(
    () => createDateInvoiceColumns(onOpenAmountClick),
    [onOpenAmountClick]
  );

  const invoiceDateTableData = useMemo(
    () => transformDateInvoiceData(data),
    [data]
  );

  const handleRowClick = (record, column) => {
    if (onRowClick) {
      onRowClick(record, column);
    }
  };

  return {
    invoiceDateColumns,
    invoiceDateTableData,
    handleRowClick,
  };
};
